import createWorkerFunction_ from "./createWorkerFunction_.js";
export default function createWorkerFunction(createWorkerInEnvironment, fnOrAlias) {
    // When the worker function returns a monolithic response,
    // use `resolveCall()`/`rejectCall()` functions to report the response to the main thread.
    // These functions will `resolve` or `reject` the `Promise` returned from `.call()` or `.callOnce()`.
    let resolveCall = undefined;
    let rejectCall = undefined;
    const createMethods = (start, stop, started, stopped, sendToWorker, mustHaveStarted, mustNotHaveStarted, mustNotHaveStopped) => ({
        // Calls the function. Could be used multiple times.
        call(...args) {
            mustNotHaveStopped();
            mustHaveStarted();
            if (resolveCall || rejectCall) {
                throw new Error('Previous call not finished');
            }
            // isStreamingFn = false
            return new Promise((resolve, reject) => {
                resolveCall = resolve;
                rejectCall = reject;
                sendToWorker(args);
            });
        },
        // Calls the function once.
        callOnce(...args) {
            start();
            return this.call(...args).finally(stop);
        }
    });
    // This function will be executed in the worker thread.
    // It will be stringified and injected in the worker source code.
    // Therefore, it should be a "self-contained" function,
    // i.e. it shouldn't reference anything outside of its body.
    const createInputHandler = (fn, send) => {
        const isPromise = (anything) => {
            return (anything !== null &&
                typeof anything === 'object' &&
                typeof anything.then === 'function');
        };
        return (args) => {
            const result = fn(...args);
            if (isPromise(result)) {
                result.then(send);
            }
            else {
                send(result);
            }
        };
    };
    // This function will be executed in the main thread.
    // Currently, we are in the main thread.
    const handleError = (error) => {
        if (rejectCall) {
            rejectCall(error);
            resolveCall = undefined;
            rejectCall = undefined;
        }
        else {
            throw new Error('`reject` callback not found');
        }
    };
    // This function will be executed in the main thread.
    // Currently, we are in the main thread.
    const handleOutput = (output) => {
        if (resolveCall) {
            resolveCall(output);
            resolveCall = undefined;
            rejectCall = undefined;
        }
        else {
            throw new Error('`resolve` callback not found');
        }
    };
    return createWorkerFunction_(createWorkerInEnvironment, fnOrAlias, createMethods, createInputHandler, handleError, handleOutput);
}
